import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowDown, ArrowUp, Search, X } from 'lucide-react';
import { SiteFooter, SiteHeader } from '@/components/SiteHeader';
import { CoverPicker } from '@/components/CoverPicker';
import { Avatar, displayNameOf } from '@/components/Avatar';
import { useAuthor } from '@/hooks/useAuthor';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useFollowPack } from '@/hooks/useFollowPacks';
import { useProfileSearch } from '@/hooks/useProfileSearch';
import { useToast } from '@/hooks/useToast';
import { buildFollowPackTags, generatePackId } from '@/lib/followPacks';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useDebounce } from '@/hooks/useDebounce';

interface DraftMember {
  pubkey: string;
  name: string;
  picture: string;
}

const MAX_MEMBERS = 200;

function MemberDraftRow({ member, onRemove, onMove }: {
  member: DraftMember;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
}) {
  return (
    <li className="flex items-center gap-3 rounded-xl border bg-card p-2.5">
      <Avatar pubkey={member.pubkey} src={member.picture || undefined} alt={member.name} size="md" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{member.name}</p>
        <p className="truncate font-mono text-[11px] text-muted-foreground">{member.pubkey.slice(0, 16)}…</p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Move up" onClick={() => onMove(-1)}>
          <ArrowUp className="size-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Move down" onClick={() => onMove(1)}>
          <ArrowDown className="size-4" />
        </Button>
        <Button type="button" variant="ghost" size="icon" className="size-8" aria-label="Remove" onClick={onRemove}>
          <X className="size-4" />
        </Button>
      </div>
    </li>
  );
}

function SearchResultRow({ pubkey, name, picture, added, onAdd }: {
  pubkey: string;
  name: string;
  picture: string;
  added: boolean;
  onAdd: () => void;
}) {
  return (
    <li className="flex items-center gap-3 px-2 py-2">
      <Avatar pubkey={pubkey} src={picture || undefined} alt={name} size="sm" />
      <span className="min-w-0 flex-1 truncate text-sm font-medium">{name}</span>
      <Button type="button" size="sm" variant={added ? 'secondary' : 'default'} className="rounded-full" disabled={added} onClick={onAdd}>
        {added ? 'Added' : 'Add'}
      </Button>
    </li>
  );
}

export function PackEditor({ mode }: { mode: 'create' | 'edit' }) {
  useSeoMeta({
    title: mode === 'create' ? 'Create a pack — Follow Party' : 'Edit pack — Follow Party',
    description: 'Build a shareable follow pack for Nostr.',
  });

  const { user } = useCurrentUser();
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutateAsync: publish, isPending: isPublishing } = useNostrPublish();

  const params = useParams<{ pubkey: string; d: string }>();
  const editPubkey = params.pubkey && /^[0-9a-f]{64}$/i.test(params.pubkey) ? params.pubkey.toLowerCase() : undefined;
  const editD = params.d ? decodeURIComponent(params.d) : undefined;
  const { data: existing, isLoading: isLoadingExisting } = useFollowPack(
    mode === 'edit' ? editPubkey : undefined,
    mode === 'edit' ? editD : undefined,
  );

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [coverUrl, setCoverUrl] = useState('');
  const [members, setMembers] = useState<DraftMember[]>([]);
  const [hydrated, setHydrated] = useState(mode === 'create');
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 350);
  const { data: results, isFetching: isSearching } = useProfileSearch(debouncedQuery);

  useEffect(() => {
    if (mode === 'edit' && existing?.pack && !hydrated) {
      setName(existing.pack.name);
      setDescription(existing.pack.description);
      setCoverUrl(existing.pack.coverImageUrl);
      setMembers(existing.pack.members.map((m) => ({ pubkey: m.pubkey, name: `${m.pubkey.slice(0, 8)}…`, picture: '' })));
      setHydrated(true);
    }
  }, [existing?.pack, hydrated, mode]);

  const memberSet = useMemo(() => new Set(members.map((m) => m.pubkey)), [members]);

  const addMember = (pubkey: string, fallbackName?: string, picture?: string) => {
    if (memberSet.has(pubkey)) return;
    if (members.length >= MAX_MEMBERS) {
      toast({ title: `Packs hold up to ${MAX_MEMBERS} people`, variant: 'destructive' });
      return;
    }
    setMembers((prev) => [...prev, { pubkey, name: fallbackName || `${pubkey.slice(0, 8)}…`, picture: picture || '' }]);
    setQuery('');
  };

  const canPublish = user && name.trim().length > 0 && members.length > 0 && !isPublishing;

  const onPublish = async () => {
    if (!user) {
      toast({ title: 'Log in first', description: 'You need to be logged in to publish a pack.' });
      return;
    }
    if (!name.trim()) {
      toast({ title: 'Give your pack a name', variant: 'destructive' });
      return;
    }
    if (members.length === 0) {
      toast({ title: 'Add at least one person', variant: 'destructive' });
      return;
    }
    const id = mode === 'edit' && existing?.pack ? existing.pack.id : generatePackId();
    await publish({
      kind: 39089,
      content: '',
      tags: buildFollowPackTags({
        id,
        name,
        description,
        coverImageUrl: coverUrl,
        members: members.map((m) => ({ pubkey: m.pubkey })),
      }),
    });
    await queryClient.invalidateQueries({ queryKey: ['follow-packs'] });
    await queryClient.invalidateQueries({ queryKey: ['follow-pack'] });
    toast({ title: mode === 'edit' ? 'Pack updated' : 'Pack published!' });
    navigate(`/pack/${user.pubkey}/${encodeURIComponent(id)}`);
  };

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col">
        <SiteHeader />
        <main className="container flex-1 py-16">
          <Card className="mx-auto max-w-md border-dashed">
            <CardContent className="px-8 py-12 text-center">
              <p className="text-muted-foreground">Log in to {mode === 'create' ? 'create' : 'edit'} a pack.</p>
            </CardContent>
          </Card>
        </main>
        <SiteFooter />
      </div>
    );
  }

  if (mode === 'edit') {
    if (isLoadingExisting || !hydrated) {
      return (
        <div className="flex min-h-screen flex-col">
          <SiteHeader />
          <main className="container max-w-3xl flex-1 space-y-4 py-8">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-64 w-full" />
          </main>
          <SiteFooter />
        </div>
      );
    }
    if (!existing?.pack) {
      return (
        <div className="flex min-h-screen flex-col">
          <SiteHeader />
          <main className="container flex-1 py-16">
            <Card className="mx-auto max-w-md border-dashed">
              <CardContent className="px-8 py-12 text-center">
                <p className="text-muted-foreground">Pack not found or you’re not the owner.</p>
                <Button asChild className="mt-4 rounded-full">
                  <Link to="/">Back home</Link>
                </Button>
              </CardContent>
            </Card>
          </main>
          <SiteFooter />
        </div>
      );
    }
    if (existing.pack.pubkey !== user.pubkey) {
      return (
        <div className="flex min-h-screen flex-col">
          <SiteHeader />
          <main className="container flex-1 py-16">
            <Card className="mx-auto max-w-md border-dashed">
              <CardContent className="px-8 py-12 text-center">
                <p className="text-muted-foreground">Only the pack owner can edit it.</p>
                <Button asChild className="mt-4 rounded-full">
                  <Link to="/">Back home</Link>
                </Button>
              </CardContent>
            </Card>
          </main>
          <SiteFooter />
        </div>
      );
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="container max-w-3xl flex-1 py-8">
        <Button asChild variant="ghost" className="mb-4 rounded-full">
          <Link to={mode === 'edit' && existing?.pack ? `/pack/${existing.pack.pubkey}/${encodeURIComponent(existing.pack.id)}` : '/'}>
            <ArrowLeft className="size-4" />
            {mode === 'edit' ? 'Back to pack' : 'All packs'}
          </Link>
        </Button>

        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {mode === 'create' ? (
            <>Create a <span className="font-display italic">pack</span></>
          ) : (
            <>Edit <span className="font-display italic">pack</span></>
          )}
        </h1>
        <p className="mt-2 text-muted-foreground">
          Curate people others should follow. Publishing writes a portable kind 39089 event to relays.
        </p>

        <div className="mt-6 grid gap-6">
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pack-name">Title</Label>
                <Input
                  id="pack-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Bitcoin designers"
                  maxLength={80}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pack-desc">Description</Label>
                <Textarea
                  id="pack-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Who is this pack for? What ties these people together?"
                  rows={3}
                  maxLength={500}
                  className="rounded-xl"
                />
              </div>
              <CoverPicker
                coverUrl={coverUrl}
                onChange={setCoverUrl}
                packName={name}
                memberPubkeys={members.map((m) => m.pubkey)}
              />
            </CardContent>
          </Card>

          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg">People ({members.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, or paste npub / hex…"
                  className="rounded-full pl-10"
                  aria-label="Search users to add"
                />
              </div>
              {debouncedQuery.trim().length >= 2 || query.trim().length >= 2 ? (
                <div className="rounded-xl border">
                  {isSearching ? (
                    <p className="px-4 py-3 text-sm text-muted-foreground">Searching relays…</p>
                  ) : results && results.length > 0 ? (
                    <ul className="divide-y">
                      {results.map((r) => (
                        <SearchResultRow
                          key={r.pubkey}
                          pubkey={r.pubkey}
                          name={r.name}
                          picture={r.picture}
                          added={memberSet.has(r.pubkey)}
                          onAdd={() => addMember(r.pubkey, r.name, r.picture)}
                        />
                      ))}
                    </ul>
                  ) : (
                    <p className="px-4 py-3 text-sm text-muted-foreground">No matches. Try another name or paste an npub.</p>
                  )}
                </div>
              ) : null}

              {members.length === 0 ? (
                <p className="rounded-xl border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
                  Nobody here yet — search above to add people.
                </p>
              ) : (
                <ul className="space-y-2">
                  {members.map((m) => (
                    <MemberDraftRow
                      key={m.pubkey}
                      member={m}
                      onRemove={() => setMembers((prev) => prev.filter((x) => x.pubkey !== m.pubkey))}
                      onMove={(dir) => {
                        setMembers((prev) => {
                          const j = prev.findIndex((x) => x.pubkey === m.pubkey);
                          const k = j + dir;
                          if (j < 0 || k < 0 || k >= prev.length) return prev;
                          const next = [...prev];
                          [next[j], next[k]] = [next[k], next[j]];
                          return next;
                        });
                      }}
                    />
                  ))}
                </ul>
              )}
              <div className="hidden" aria-hidden>
                {members.filter((m) => !m.picture).slice(0, 30).map((m) => (
                  <HydrateName key={m.pubkey} pubkey={m.pubkey} onName={(n, p) => {
                    setMembers((prev) => prev.map((x) => (x.pubkey === m.pubkey ? { ...x, name: n, picture: p } : x)));
                  }} />
                ))}
              </div>
            </CardContent>
          </Card>

          <Button size="lg" className="rounded-full" disabled={!canPublish} onClick={() => void onPublish()}>
            {isPublishing ? 'Publishing…' : mode === 'edit' ? `Save changes (${members.length})` : `Publish pack (${members.length})`}
          </Button>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function HydrateName({ pubkey, onName }: { pubkey: string; onName: (name: string, picture: string) => void }) {
  const { data } = useAuthor(pubkey);
  useEffect(() => {
    if (data?.metadata) {
      onName(displayNameOf(data.metadata, pubkey), data.metadata.picture || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.metadata]);
  return null;
}

export default function CreatePage() {
  return <PackEditor mode="create" />;
}
